import React, { useState, useEffect, useCallback } from "react";
import { Container, Modal, Button, Alert } from "react-bootstrap";
import axios from "axios";
import CustomButton from "../../components/button";
import SearchBar from "../../components/searchbar";
import Footer from "../../components/footer";
import SpinnerComp from "../../components/spinner";
import DetailsTable from "../../components/table";
import { toast, ToastContainer } from "react-toastify"; // For toast notifications
import "react-toastify/dist/ReactToastify.css";
import debounce from "lodash.debounce";
import { ReactComponent as ArrowUpRight } from '../../static/images/svg/Arrow up right.svg';
import OffCanvasComp from '../../components/shipment-status-form-canva/index';
import HistoryOffCanvasComp from '../../components/offcanvas';


const UpdateStatus = ({ user, isHist=true }) => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItem, setTotalItem] = useState(0);
  const [orderItem, setOrderItem] = useState();
  const [show, setShow] = useState(false);

  const pageSize = 8;

  // Fetch users with debounce to avoid excessive API calls
  const fetchUsers = useCallback(
    debounce(async (searchQuery, currentPage) => {
      try {
        setLoading(true);

        const response = await axios.get(`${process.env.REACT_APP_DEV_BACKEND_URL}/shipment/rider-assignments`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            search: searchQuery,
            ...(isHist ? {} : { status: "in transit" }), // Omit status if isHist is true
            page: currentPage,
            pageSize: pageSize,
          },
        });

        if (response.status === 200) {
          const { shipments, pagination } = response.data;
          setUsers(shipments);
          setTotalPages(pagination.totalPages || 1);
          setTotalItem(pagination.totalItems || 0);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    }, 1000),
    [user.token, isHist]
  );

  // Handle search input
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to the first page on search
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShouldFetchAgain = () => {
    fetchUsers(searchQuery, currentPage);
  };

  // Open detailed view based on isHist
  const handleButtonClick = (item) => {
    setOrderItem(item);
    setShow(true);
  };

  // Fetch users on component mount and whenever dependencies change
  useEffect(() => {
    fetchUsers(searchQuery, currentPage);
  }, [searchQuery, currentPage, fetchUsers]);

  // Define columns for the table
  const columns = [
    {
      header: "Barcode#",
      render: (item) => item.shipmentId,
    },
    {
      header: "Status",
      render: (item) => item.status,
    },
    {
      header: "Receiver",
      render: (item) => item.receiverName,
    },
    {
      header: "Phone",
      render: (item) => (
        <a
          href={`https://wa.me/+${item.phoneNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "#005A9C" }}
        >
          +{item.phoneNumber}
        </a>
      ),
    },
    {
      header: "Address",
      render: (item) => item.address,
    },
    {
      header: "Intended Date",
      render: (item) => {
        const lastHistory = item.trackingHistory[item.trackingHistory.length - 1];
        if (lastHistory?.newIntendedDate) {
          const date = new Date(lastHistory.newIntendedDate);
          return `${date.getDate().toString().padStart(2, "0")}/${
            (date.getMonth() + 1).toString().padStart(2, "0")
          }/${date.getFullYear()}`;
        }
        return "N/A";
      },
    },
    {
      header: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center gap-2">
          <ArrowUpRight
            onClick={() => handleButtonClick(item)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <SpinnerComp />
      ) : (
        <Container fluid className="px-4 bg-light p-4" style={{ minHeight: "92vh" }}>
          <h2 style={{ color: "#005A9C" }}>{isHist ? "Shipment History" : "Rider Assignments"}</h2>
          <ToastContainer />

          <SearchBar
            placeholder="Search by Shipment ID..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <div
            className="border my-3"
            style={{ height: "27rem", overflowY: "auto" }}
          >
            {users.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100%" }}
              >
                <Alert
                  className="text-center"
                  style={{ width: "50%", backgroundColor: "#005A9C", color: "white" }}
                >
                  No shipments found.
                </Alert>
              </div>
            ) : (
              <DetailsTable isTransparent={true} data={users} columns={columns} />
            )}
          </div>

          <Footer
            className={"d-flex justify-content-between align-items-center pt-3"}
            text={`${totalItem} Total Count`}
            pageSize={pageSize}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
          />
        </Container>
      )}
      {show && (
        isHist ? (
          <HistoryOffCanvasComp
          placement={'end'}
          show={show}
          setShow={setShow}
          orderItem={orderItem}
          name={user.name}
          token={user.token}
          />
        ) : (
          <OffCanvasComp
            placement={"end"}
            show={show}
            setShow={setShow}
            product={orderItem}
            handleShouldFetchAgain={handleShouldFetchAgain}
            name={user.name}
            token={user.token}
          />
        )
      )}
    </>
  );
};

export default UpdateStatus;
